/**
 * Permission helpers for the Academic Session Scanner.
 *
 * The DB-managed list of bypass roles lives in `qr_bypass_roles` and is loaded
 * by the ScannerTab. These helpers keep the rule pure and testable.
 *
 * Rule: a user can bypass the QR visibility gate (and thus enable the
 * Start Scanning button when there are no active visibility rules) when:
 *   - they have the `superadmin` role, OR
 *   - they have at least one role present in the academic bypass list.
 */

export type RoleName = string;

/** All Class Representative role names. */
export const CR_ROLES: RoleName[] = [
  "cr_cmt4", "cr_cmt5", "cr_cmt6",
  "cr_pst4", "cr_pst5", "cr_pst6",
  "cr_sw4", "cr_sw5", "cr_sw6",
];

/**
 * Default bypass list seeded into `qr_bypass_roles` for `qr_type='academic'`.
 * Used as a fallback in tests when the DB-driven list is unavailable.
 */
export const DEFAULT_ACADEMIC_BYPASS_ROLES: RoleName[] = [
  "superadmin",
  "principal",
  "deputy_principal",
  "head_academic",
  "head_administration",
  "it_incharge",
  "security_man",
  "hod_cmt",
  "hod_pst",
  "hod_sw",
  ...CR_ROLES,
];

/** Returns true if the user can bypass the academic QR visibility gate. */
export function canBypassAcademicGate(
  userRoles: RoleName[],
  bypassRoles: RoleName[],
): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  if (userRoles.includes("superadmin")) return true;
  return userRoles.some((r) => bypassRoles.includes(r));
}

export interface StartScanGateInput {
  userRoles: RoleName[];
  bypassRoles: RoleName[];
  /** Result of the QR visibility rule evaluation (time/geo/role rules). */
  qrVisibilityVisible: boolean;
  /** Whether a session is selected, open, and dated for today. */
  sessionValidForScan: boolean;
}

/**
 * Final enable rule for the Start Scanning button. Mirrors the logic in
 * `ScannerTab` (Attendance page).
 */
export function canStartScanning(input: StartScanGateInput): boolean {
  const bypass = canBypassAcademicGate(input.userRoles, input.bypassRoles);
  const effectiveVisible = bypass || input.qrVisibilityVisible;
  return effectiveVisible && input.sessionValidForScan;
}
